import { useEffect, useRef, useState } from "react";
import { ImageSrc } from "../components/utils/ImageSrc";
import { Group, Layer, Line, Stage } from "react-konva";
import { azureSASTokenToUrl } from "../components/formatted/FormattedAzureSasToken";
import { getDimensions } from "../components/formatted/CalculateImageDimensions";
import { formattedColorMaterial } from "../components/formatted/FormattedText";
import useWindowDimensions from "../components/formatted/UseWindowsDimensions";

export const AppImageTreatment = ({
  imageUrl,
  xBox,
  yBox,
  wBox,
  hBox,
  detectedObjects,
  materialToDisplay,
  compact = false,
  fitCrop = false,
}) => {
  const [imgWidth, setImgWidth] = useState();
  const [imgHeight, setImgHeight] = useState();
  const [wasteObjectsList, setWasteObjectsList] = useState([]);
  const [zoomFactor, setZoomFactor] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const lastDistRef = useRef(null);
  const lastPanPosRef = useRef(null);
  const stageRef = useRef(null);
  const stageWrapRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const { widthWindow } = useWindowDimensions();
  const defaultCompactHeight = Math.min(
    400,
    Math.max(320, Math.round(widthWindow * 0.84)),
  );
  let imgDivHeight = compact ? defaultCompactHeight : 380;
  const imgDivWidth =
    containerWidth > 0 ? containerWidth : Math.max(0, widthWindow - 28);

  useEffect(() => {
    setWasteObjectsList(detectedObjects || []);
  }, [detectedObjects]);

  useEffect(() => {
    if (!imageUrl) {
      setImgWidth(undefined);
      setImgHeight(undefined);
      return;
    }
    getDimensions(azureSASTokenToUrl(imageUrl), setImgWidth, setImgHeight);
  }, [imageUrl]);

  useEffect(() => {
    const element = stageWrapRef.current;
    if (!element) {
      return undefined;
    }

    const updateWidth = () => {
      setContainerWidth(element.clientWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setZoomFactor(1);
    setStagePos({ x: 0, y: 0 });
  }, [imageUrl, containerWidth, xBox, yBox, wBox, hBox]);

  let local_w_box = wBox + 80;
  let local_h_box = hBox + 80;
  let local_x_box = xBox - 40;
  let local_y_box = yBox - 40;

  if (local_x_box < 0) {
    local_w_box -= Math.abs(local_x_box);
    local_x_box = 0;
  }
  if (local_y_box < 0) {
    local_h_box -= Math.abs(local_y_box);
    local_y_box = 0;
  }
  if (imgWidth > 0 && imgHeight > 0) {
    local_w_box = Math.min(local_w_box, imgWidth - local_x_box);
    local_h_box = Math.min(local_h_box, imgHeight - local_y_box);
  }

  const hasDimensions =
    imgWidth > 0 && imgHeight > 0 && local_w_box > 0 && local_h_box > 0;

  let baseScale = 1;

  if (hasDimensions) {
    if (fitCrop) {
      baseScale = imgDivWidth / local_w_box;
      imgDivHeight = Math.ceil(local_h_box * baseScale);
      const maxHeight = Math.min(420, Math.round(widthWindow * 1.05));
      if (imgDivHeight > maxHeight) {
        baseScale = maxHeight / local_h_box;
        imgDivHeight = maxHeight;
      }
    } else {
      baseScale = Math.min(
        imgDivWidth / local_w_box,
        imgDivHeight / local_h_box,
      );
    }
  }

  const handleTouchMove = (e) => {
    const touches = e.evt.touches;
    if (!touches || touches.length === 0) return;

    if (touches.length === 2) {
      e.evt.preventDefault();
      lastPanPosRef.current = null;

      const dist = Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY,
      );

      if (!lastDistRef.current) {
        lastDistRef.current = dist;
        return;
      }

      const stage = stageRef.current;
      if (!stage) {
        lastDistRef.current = dist;
        return;
      }

      const oldScale = zoomFactor;
      const scaleBy = dist / lastDistRef.current;
      const newScale = Math.min(4, Math.max(1, oldScale * scaleBy));

      const pointer = stage.getPointerPosition();
      if (pointer) {
        const mousePointTo = {
          x: (pointer.x - stagePos.x) / oldScale,
          y: (pointer.y - stagePos.y) / oldScale,
        };

        const newPos = {
          x: pointer.x - mousePointTo.x * newScale,
          y: pointer.y - mousePointTo.y * newScale,
        };

        setStagePos(newPos);
        setZoomFactor(newScale);
      } else {
        setZoomFactor(newScale);
      }

      lastDistRef.current = dist;
      return;
    }

    if (touches.length === 1) {
      e.evt.preventDefault();
      const touch = touches[0];
      const currentPos = { x: touch.clientX, y: touch.clientY };

      if (!lastPanPosRef.current) {
        lastPanPosRef.current = currentPos;
        return;
      }

      const dx = currentPos.x - lastPanPosRef.current.x;
      const dy = currentPos.y - lastPanPosRef.current.y;

      setStagePos((prev) => ({
        x: prev.x + dx,
        y: prev.y + dy,
      }));

      lastPanPosRef.current = currentPos;
    }
  };

  const handleTouchEnd = () => {
    const evtTouches =
      (typeof window !== "undefined" && window.event && window.event.touches) ||
      [];
    if (!evtTouches || evtTouches.length === 0) {
      lastDistRef.current = null;
      lastPanPosRef.current = null;
    }
  };

  return (
    <section
      className={[
        compact
          ? "notification-image-treatment notification-image-treatment-compact"
          : "notification-image-treatment",
        fitCrop ? "notification-image-treatment-fit-crop" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        ref={stageWrapRef}
        className="notification-image-treatment-stage-wrap"
      >
        <div
          className="absolute z-10 opacity-0"
          style={{
            width: imgDivWidth,
            height: imgDivHeight,
            pointerEvents: "none",
          }}
        />
        {hasDimensions && imgDivWidth > 0 ? (
          <Stage
            ref={stageRef}
            width={imgDivWidth}
            height={imgDivHeight}
            scaleX={zoomFactor}
            scaleY={zoomFactor}
            x={stagePos.x}
            y={stagePos.y}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <Layer>
              <Group
                clip={{
                  x: local_x_box,
                  y: local_y_box,
                  width: local_w_box,
                  height: local_h_box,
                }}
                x={
                  (imgDivWidth - local_w_box * baseScale) / 2 -
                  local_x_box * baseScale
                }
                y={
                  (imgDivHeight - local_h_box * baseScale) / 2 -
                  local_y_box * baseScale
                }
                scaleX={baseScale}
                scaleY={baseScale}
              >
                <ImageSrc
                  src={azureSASTokenToUrl(imageUrl)}
                  width={imgWidth}
                  height={imgHeight}
                />
                {Array.isArray(wasteObjectsList) &&
                  wasteObjectsList.length > 0 &&
                  wasteObjectsList.map((anomalie) => {
                    if (
                      "cls" in anomalie === false ||
                      "points_konva" in anomalie === false
                    ) {
                      return null;
                    }
                    const anomalyType = anomalie.cls.toLowerCase();
                    const isSelected = materialToDisplay.some(
                      (item) => item.value === anomalyType,
                    );

                    if (!isSelected) {
                      return null;
                    }

                    return (
                      <Group key={anomalie._id}>
                        <Line
                          points={anomalie.points_konva}
                          opacity={0.3}
                          strokeWidth={0}
                          closed={true}
                          fill={formattedColorMaterial(anomalie.cls)}
                        />
                        <Line
                          points={anomalie.points_konva}
                          opacity={1}
                          strokeWidth={5}
                          closed={true}
                          stroke={formattedColorMaterial(anomalie.cls)}
                        />
                      </Group>
                    );
                  })}
              </Group>
            </Layer>
          </Stage>
        ) : null}
      </div>
    </section>
  );
};
